import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getOverview, getRevenueStats, getUserStats, getCourseStats, getEngagementStats,
} from '../../api/stats';
import './Statistics.css';
import PageHead from '../../components/PageHead/PageHead';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import SubTabs from '../../components/SubTabs/SubTabs';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import {
  ChartFrame, ChartTooltip, AreaGradient, areaProps, barProps, seriesColor, PIE_PROPS,
  CHART_MARGIN, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, CURSOR_PROPS, BAR_CURSOR_PROPS,
  compactTick, numberTick, dateTick,
} from '../../components/charts/chartTheme';
import {
  BarChart3, Users, GraduationCap, Star, Banknote, BookOpen,
  Eye, Timer, FileText, Target, MessageSquare, Smile,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { useI18n } from '../../i18n/I18nContext';
import { usePermissions } from '../../hooks/usePermissions';
import { isAccountAdmin } from '../../utils/scope';
import { useScrollFade } from '../../hooks/useScrollFade';
import { statusLabel } from '../../utils/labels';
import { fillDays, niceTicks } from '../../utils/dailySeries';
import {
  formatNumber, formatPercent, formatMoney, formatMoneyCompact, formatDate,
} from '../../utils/datetime';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

// GET /stats/users groups by the raw UserRole enum (STUDENT/TEACHER/MENTOR/
// MODERATOR/ADMIN) — reuse the labels the Team page already has for the
// three roles it shares, add the two that are new here.
const ROLE_LABEL_KEY = {
  ADMIN: 'team.roleAdmin',
  TEACHER: 'team.roleTeacher',
  STUDENT: 'team.roleStudent',
  MENTOR: 'statistics.roleMentor',
  MODERATOR: 'statistics.roleModerator',
};

// Providers are written the way the brands write themselves; Payme's merchant API is "PAYCOM".
const PROVIDER_LABEL_KEY = { CLICK: 'statistics.providerClick', PAYCOM: 'statistics.providerPayme', PAYME: 'statistics.providerPayme' };

const PERIODS = ['7d', '30d', '90d', '1y'];

const hasValue = (v) => v !== null && v !== undefined && v !== '';

/* ─── Small parts ─────────────────────────────────────────────────────────── */

function RankBadge({ rank }) {
  return <span className={`stats-rank${rank <= 3 ? ' is-top' : ''}`}>{rank}</span>;
}

// A section: a heading (with a quiet note at its right) over the cards that belong to it.
function Section({ id, title, aside, children }) {
  return (
    <section className="stats-section" aria-labelledby={`stats-${id}`}>
      <div className="stats-section-head">
        <h2 className="stats-section-title" id={`stats-${id}`}>{title}</h2>
        {aside && <span className="stats-section-aside">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

// One section whose request failed: it says so in place and offers the retry; the rest of the page
// keeps what it has.
function SectionError({ onRetry }) {
  const { t } = useI18n();
  return (
    <InlineEmpty
      kind="error"
      surface="card"
      title={t('ui.errorTitle')}
      description={t('ui.errorDescription')}
      action={(
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onRetry}>
          {t('ui.retryAgain')}
        </button>
      )}
    />
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Main component
   ════════════════════════════════════════════════════════════════════════════ */
function Statistics() {
  const { t, lang } = useI18n();
  const { canViewMoney, user } = usePermissions();
  // Revenue, ARPU and the provider split are money: a role that may not see money (the oversight
  // role, a course owner without a team role) gets the rest of the page.
  const showMoney = canViewMoney();
  const wholePlatform = isAccountAdmin(user);

  const roleLabel = useCallback(
    (role) => t(ROLE_LABEL_KEY[String(role || '').toUpperCase()] || 'team.roleStudent'),
    [t],
  );
  const providerLabel = useCallback((raw) => {
    const key = PROVIDER_LABEL_KEY[String(raw || '').toUpperCase()];
    return key ? t(key) : (raw || t('statistics.unknownProvider'));
  }, [t]);

  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true); // the first load only: later ones keep the page
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState({}); // { revenue: true, users: true, ... } the requests that failed

  const [overview, setOverview]     = useState(null);
  const [revenue, setRevenue]       = useState(null);
  const [users, setUsers]           = useState(null);
  const [courses, setCourses]       = useState(null);
  const [engagement, setEngagement] = useState(null);

  // Only the latest request may write: switching periods quickly must not let a slow answer
  // for the old period overwrite the new one.
  const latest = useRef(0);
  const periodRef = useRef('30d');

  const fetchAll = useCallback(async (p) => {
    latest.current += 1;
    const id = latest.current;
    setRefreshing(true);
    const jobs = [
      ['overview', () => getOverview()],
      ...(showMoney ? [['revenue', () => getRevenueStats({ period: p })]] : []),
      ['users', () => getUserStats({ period: p })],
      ['courses', () => getCourseStats({ period: p })],
      ['engagement', () => getEngagementStats({ period: p })],
    ];
    const results = await Promise.allSettled(jobs.map(([, run]) => run()));
    if (id !== latest.current) return;
    const got = {};
    const down = {};
    jobs.forEach(([name], i) => {
      if (results[i].status === 'fulfilled') got[name] = results[i].value;
      else down[name] = true;
    });
    setOverview(got.overview ?? null);
    setRevenue(got.revenue ?? null);
    setUsers(got.users ?? null);
    setCourses(got.courses ?? null);
    setEngagement(got.engagement ?? null);
    setFailed(down);
    setLoading(false);
    setRefreshing(false);
  }, [showMoney]);

  useEffect(() => { fetchAll(periodRef.current); }, [fetchAll]);

  const handlePeriod = (p) => {
    periodRef.current = p;
    setPeriod(p);
    fetchAll(p);
  };
  const retry = () => fetchAll(periodRef.current);
  // One failing endpoint leaves its own section as an error card; every one failing is an error page.
  const everyFailed = Object.keys(failed).length > 0 && Object.keys(failed).length === (showMoney ? 5 : 4);

  const periodTabs = useMemo(
    () => PERIODS.map((p) => ({ key: p, label: t(`statistics.period${p}`) })),
    [t],
  );

  const count = useCallback((v) => (hasValue(v) ? formatNumber(v, { lang }) : '—'), [lang]);
  const money = useCallback((v) => formatMoney(v, 'UZS', { lang, free: false }), [lang]);
  const moneyCompact = useCallback(
    (v) => (hasValue(v) ? formatMoneyCompact(v, 'UZS', { lang }) : '—'),
    [lang],
  );

  /* ── Derived data ──────────────────────────────────────────────────────── */
  // The API sends UTC calendar days with something in them; the charts want every day of the period.
  const revenueByDay = useMemo(() => fillDays(
    (revenue?.revenueByDay || []).map((r) => ({ date: r.date, amount: Number(r.amount ?? r.revenue) || 0 })),
    period,
    'amount',
  ), [revenue, period]);
  const periodRevenue = revenue?.periodRevenue ?? revenueByDay.reduce((sum, p) => sum + p.amount, 0);
  const revenueAxis = useMemo(() => niceTicks(Math.max(0, ...revenueByDay.map((p) => p.amount))), [revenueByDay]);

  const userGrowth = useMemo(() => fillDays(
    (users?.userGrowth || []).map((r) => ({ date: r.date, count: Number(r.count) || 0 })),
    period,
    'count',
  ), [users, period]);
  const growthAxis = useMemo(
    () => niceTicks(Math.max(0, ...userGrowth.map((p) => p.count)), { intervals: 4, integer: true }),
    [userGrowth],
  );

  const usersByRole = (users?.usersByRole || []).map((r, i) => ({
    ...r, roleLabel: roleLabel(r.role), fill: seriesColor(i),
  }));
  const totalByRole = usersByRole.reduce((s, r) => s + (r.count || 0), 0);

  // One table of courses: enrollments and revenue come from the top-courses list, the completion
  // rate from the completion list (matched by course id; a course missing there shows a dash).
  const courseRows = useMemo(() => {
    const rates = new Map((courses?.completionRates || []).map((r) => [r.courseId, Number(r.rate) || 0]));
    return (courses?.topCourses || []).slice(0, 10).map((c, i) => ({
      key: c.id || i,
      title: c.title || t('statistics.unknownCourse'),
      enrollments: c.enrollments,
      revenue: c.revenue,
      rate: rates.has(c.id) ? rates.get(c.id) : null,
    }));
  }, [courses, t]);

  const providers = useMemo(() => {
    const list = (revenue?.revenueByProvider || []).map((p) => ({
      name: providerLabel(p.provider || p.name),
      amount: Number(p.total ?? p.amount) || 0,
    }));
    const sum = list.reduce((s, p) => s + p.amount, 0);
    return list.map((p) => ({ ...p, share: sum ? Math.round((p.amount / sum) * 100) : 0 }));
  }, [revenue, providerLabel]);

  const totalRevenue = overview?.totalRevenue ?? revenue?.totalRevenue;
  const completionRate = overview?.completionRate;
  const totalUsers = overview?.totalUsers ?? users?.totalUsers;
  const activeUsers = users?.activeUsers ?? overview?.activeUsers;
  const arpu = users?.arpu ?? revenue?.arpu;
  const watchMinutes = engagement?.averageWatchTime ? engagement.averageWatchTime / 60 : null;
  const periodLabel = t(`statistics.period${period}`);
  // "so'nggi 30 kun ichida": the engagement figures answer for the period picked above
  const inPeriod = t('statistics.kpiNewUsersSub', { period: periodLabel });
  const tableScroll = useScrollFade({ watch: courseRows.length });
  // "5   71.4%": the share of all orders; with no orders there is nothing to take a share of
  const orderShare = (n) => (revenue?.totalOrders > 0 ? formatPercent((n / revenue.totalOrders) * 100, { lang }) : '');
  // The order counts are all-time, so the average is too: what the completed orders brought in, per order.
  // (The API's own averageOrderValue covers only the picked period: beside all-time counts it would mix scopes.)
  const averageOrder = revenue?.completedOrders > 0 && hasValue(totalRevenue)
    ? Number(totalRevenue) / revenue.completedOrders
    : null;

  /* ── Page ─────────────────────────────────────────────────────────────── */
  let body;
  if (loading) {
    // The loaded page's own layout, with bones in the cards: nothing jumps when the data lands
    body = (
      <div className="ui-stack ui-stack--lg stats-body" role="status" aria-busy="true" aria-label={t('statistics.title')}>
        <div className="kpi-row stats-kpis">
          {Array.from({ length: 4 }).map((_, i) => <MetricCard key={i} loading />)}
        </div>
        <div aria-hidden="true">
          <div className="ui-skel stats-skel-heading" />
          <div className="page-grid page-grid--rail">
            <section className="section-card stats-chart-card">
              <div className="ui-skel stats-skel-line stats-skel-title" />
              <div className="ui-skel stats-skel-line stats-skel-sub" />
              <div className="ui-chart ui-skel stats-skel-chart" />
            </section>
            <div className="col-rail">
              {[2, 3].map((rows) => (
                <section key={rows} className="section-card">
                  <div className="ui-skel stats-skel-line stats-skel-title" />
                  {Array.from({ length: rows }).map((_, i) => <div key={i} className="ui-skel stats-skel-line stats-skel-row" />)}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  } else if (everyFailed) {
    body = <PageEmptyState variant="error" size="lg" onRetry={retry} />;
  } else {
    body = (
      <div className={`ui-stack ui-stack--lg stats-body${refreshing ? ' is-refreshing' : ''}`} aria-busy={refreshing}>
        {/* ── Four tiles: who, what they pay, what they study, what they think ─ */}
        <div className="kpi-row stats-kpis">
          <MetricCard
            title={t('statistics.kpiTotalUsers')}
            value={count(totalUsers)}
            change={t('statistics.kpiActiveSub', { count: count(activeUsers) })}
            icon={<Users size={20} />}
            tone="info"
          />
          {showMoney ? (
            <MetricCard
              title={t('statistics.kpiTotalRevenue')}
              value={moneyCompact(totalRevenue)}
              valueTitle={hasValue(totalRevenue) ? money(totalRevenue) : undefined}
              change={t('statistics.kpiOrdersSub', { count: count(revenue?.totalOrders) })}
              icon={<Banknote size={20} />}
              tone="success"
            />
          ) : (
            <MetricCard
              title={t('statistics.kpiTotalCourses')}
              value={count(overview?.totalCourses ?? courses?.totalCourses)}
              change={t('statistics.kpiPublishedSub', { count: count(courses?.publishedCourses) })}
              icon={<BookOpen size={20} />}
              tone="teal"
            />
          )}
          <MetricCard
            title={t('statistics.kpiTotalEnrollments')}
            value={count(overview?.totalEnrollments ?? courses?.totalEnrollments)}
            change={hasValue(completionRate) ? t('statistics.kpiCompletionRateSub', { pct: formatPercent(completionRate, { lang }) }) : undefined}
            icon={<GraduationCap size={20} />}
            tone="purple"
          />
          <MetricCard
            title={t('statistics.kpiAvgRating')}
            value={courses?.averageRating ? Number(courses.averageRating).toFixed(1) : '—'}
            change={t('statistics.kpiAvgRatingSub')}
            icon={<Star size={20} />}
            tone="brand"
          />
        </div>

        {/* ── Revenue ──────────────────────────────────────────────────────── */}
        {showMoney && (
          <Section id="revenue" title={t('statistics.sectionRevenue')}>
            {failed.revenue ? <SectionError onRetry={retry} /> : (
              <div className="page-grid page-grid--rail">
                <div className={`section-card stats-chart-card${revenueByDay.length < 2 ? ' is-empty' : ''}`}>
                  <div className="section-head">
                    <div>
                      <h3 className="section-head__title">{t('statistics.revenueTrend')}</h3>
                      <p className="section-head__desc" title={revenueByDay.length ? money(periodRevenue) : undefined}>
                        {revenueByDay.length
                          ? t('statistics.periodTotal', { period: periodLabel, amount: moneyCompact(periodRevenue) })
                          : t('statistics.revenueTrendSub')}
                      </p>
                    </div>
                  </div>
                  <ChartFrame
                    data={revenueByDay}
                    emptyKind="empty"
                    emptyTitle={t('statistics.noRevenueTitle')}
                    emptyDescription={t('statistics.noRevenueDesc')}
                  >
                    <AreaChart data={revenueByDay} margin={CHART_MARGIN}>
                      <defs><AreaGradient id="statsRevenue" color={seriesColor(0)} /></defs>
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis dataKey="date" {...X_AXIS_PROPS} tickFormatter={dateTick} />
                      <YAxis {...Y_AXIS_PROPS} domain={revenueAxis.domain} ticks={revenueAxis.ticks} tickFormatter={compactTick} />
                      <Tooltip
                        content={<ChartTooltip valueFormatter={(v) => money(v)} labelFormatter={(l) => formatDate(l, { lang })} />}
                        cursor={CURSOR_PROPS}
                      />
                      <Area dataKey="amount" name={t('statistics.revenueSeriesName')} {...areaProps(0, 'statsRevenue')} />
                    </AreaChart>
                  </ChartFrame>
                </div>

                <div className="col-rail">
                  <div className="section-card">
                    <div className="section-head">
                      <h3 className="section-head__title">{t('statistics.byProvider')}</h3>
                    </div>
                    {providers.length === 0 ? (
                      <InlineEmpty kind="empty" title={t('statistics.noProviderData')} />
                    ) : (
                      <ul className="stats-providers">
                        {providers.map((p) => (
                          <li key={p.name}>
                            <div className="stats-provider-line">
                              <span className="stats-provider-name">{p.name}</span>
                              <span className="stats-provider-amount" title={money(p.amount)}>{moneyCompact(p.amount)}</span>
                            </div>
                            <ProgressBar value={p.share} label={`${p.name}: ${formatPercent(p.share, { lang })}`} size="sm" complete={false} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="section-card">
                    <div className="section-head">
                      <h3 className="section-head__title">{t('statistics.orders')}</h3>
                    </div>
                    <dl className="ui-kv">
                      <div className="ui-kv__row">
                        <dt>{t('statistics.totalOrders')}</dt>
                        <dd>{count(revenue?.totalOrders)}</dd>
                      </div>
                      <div className="ui-kv__row">
                        <dt>{statusLabel(t, 'completed')}</dt>
                        <dd>{count(revenue?.completedOrders)} <span className="stats-kv-share">{orderShare(revenue?.completedOrders)}</span></dd>
                      </div>
                      <div className="ui-kv__row">
                        <dt>{statusLabel(t, 'refunded')}</dt>
                        <dd>{count(revenue?.refundedOrders)} <span className="stats-kv-share">{orderShare(revenue?.refundedOrders)}</span></dd>
                      </div>
                      <div className="ui-kv__row">
                        <dt>{t('statistics.avgOrderValue')}</dt>
                        <dd title={averageOrder === null ? undefined : money(averageOrder)}>{averageOrder === null ? '—' : moneyCompact(averageOrder)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}
          </Section>
        )}

        {/* ── Users ────────────────────────────────────────────────────────── */}
        <Section id="users" title={t('statistics.sectionUsers')}>
          {failed.users ? <SectionError onRetry={retry} /> : (
            <div className="page-grid page-grid--rail">
              <div className={`section-card stats-chart-card${userGrowth.length < 2 ? ' is-empty' : ''}`}>
                <div className="section-head">
                  <div>
                    <h3 className="section-head__title">{t('statistics.userGrowth')}</h3>
                    <p className="section-head__desc">{t('statistics.userGrowthSub')}</p>
                  </div>
                </div>
                <ChartFrame data={userGrowth} emptyKind="empty" emptyTitle={t('statistics.noGrowthData')}>
                  <BarChart data={userGrowth} margin={CHART_MARGIN}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="date" {...X_AXIS_PROPS} tickFormatter={dateTick} />
                    <YAxis {...Y_AXIS_PROPS} width={40} allowDecimals={false} domain={growthAxis.domain} ticks={growthAxis.ticks} tickFormatter={numberTick} />
                    <Tooltip
                      content={(
                        <ChartTooltip
                          valueFormatter={(v) => formatNumber(v, { lang })}
                          labelFormatter={(l) => formatDate(l, { lang })}
                        />
                      )}
                      cursor={BAR_CURSOR_PROPS}
                    />
                    <Bar dataKey="count" name={t('statistics.usersSeriesName')} {...barProps(2)} />
                  </BarChart>
                </ChartFrame>
                <dl className="stats-strip">
                  <div className="stats-strip__item">
                    <dt>{t('statistics.kpiNewUsers')}</dt>
                    <dd>
                      <span className="stats-strip__value">{count(users?.newUsers ?? overview?.newUsers)}</span>
                      <span className="stats-strip__hint">{t('statistics.kpiNewUsersSub', { period: periodLabel })}</span>
                    </dd>
                  </div>
                  <div className="stats-strip__item">
                    <dt>{t('statistics.kpiActiveUsers')}</dt>
                    <dd>
                      <span className="stats-strip__value">{count(activeUsers)}</span>
                      <span className="stats-strip__hint">
                        {totalUsers && hasValue(activeUsers)
                          ? t('statistics.activeUsersHint', { pct: formatPercent((activeUsers / totalUsers) * 100, { lang }) })
                          : t('statistics.activeUsersWindow')}
                      </span>
                    </dd>
                  </div>
                  {showMoney && (
                    <div className="stats-strip__item">
                      <dt>{t('statistics.kpiArpu')}</dt>
                      <dd>
                        <span className="stats-strip__value" title={hasValue(arpu) ? money(arpu) : undefined}>{moneyCompact(arpu)}</span>
                        <span className="stats-strip__hint">{t('statistics.kpiArpuSub')}</span>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="col-rail">
                <div className="section-card">
                  <div className="section-head">
                    <h3 className="section-head__title">{t('statistics.usersByRole')}</h3>
                  </div>
                  {usersByRole.length === 0 ? (
                    <InlineEmpty kind="empty" title={t('statistics.noRoleData')} />
                  ) : (
                    <div className="stats-roles">
                      <div className="stats-donut">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={usersByRole} {...PIE_PROPS} dataKey="count" nameKey="roleLabel" cx="50%" cy="50%">
                              {usersByRole.map((r, i) => <Cell key={i} fill={r.fill} />)}
                            </Pie>
                            <Tooltip
                              content={(
                                <ChartTooltip
                                  valueFormatter={(v) => t('statistics.usersUnit', { count: formatNumber(v, { lang }) })}
                                />
                              )}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="stats-donut-total" aria-hidden="true">
                          <strong>{count(totalByRole)}</strong>
                          <span>{t('statistics.donutTotal')}</span>
                        </div>
                      </div>
                      <ul className="stats-rows stats-legend">
                        {usersByRole.map((r, i) => (
                          <li key={i} className="stats-row">
                            <span className="stats-dot" style={{ background: r.fill }} />
                            <span className="stats-row-label">{r.roleLabel}</span>
                            <span className="stats-row-value">{count(r.count)}</span>
                            <span className="stats-row-share">{formatPercent(totalByRole ? (r.count / totalByRole) * 100 : 0, { lang })}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* ── Course performance ───────────────────────────────────────────── */}
        <Section
          id="courses"
          title={t('statistics.sectionCoursePerformance')}
          aside={courses?.totalCourses > 0
            ? t('statistics.publishedOf', { published: count(courses?.publishedCourses), total: count(courses?.totalCourses) })
            : undefined}
        >
          {failed.courses ? <SectionError onRetry={retry} /> : courseRows.length === 0 ? (
            <InlineEmpty kind="empty" surface="card" title={t('statistics.noCourseData')} />
          ) : (
            <div className="ui-table-card">
              <div
                ref={tableScroll.ref}
                className={`ui-table-scroll ${tableScroll.fade}`.trim()}
                role="region"
                aria-label={t('statistics.sectionCoursePerformance')}
                tabIndex={0}
              >
                <table className="ui-table ui-table--stack stats-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('statistics.colCourse')}</th>
                      <th scope="col" className="ui-num">{t('statistics.colEnrollments')}</th>
                      <th scope="col" className="ui-num">{t('statistics.colCompletion')}</th>
                      {showMoney && <th scope="col" className="ui-num">{t('statistics.colRevenue')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {courseRows.map((c, i) => (
                      <tr key={c.key}>
                        <td>
                          <div className="stats-course">
                            <RankBadge rank={i + 1} />
                            <span className="stats-course-title" title={c.title}>{c.title}</span>
                          </div>
                        </td>
                        <td className="ui-num" data-label={t('statistics.colEnrollments')}>{count(c.enrollments)}</td>
                        <td className="ui-num stats-col-rate" data-label={t('statistics.colCompletion')}>
                          {c.rate === null ? '—' : (
                            <span className="stats-rate">
                              <ProgressBar className="stats-rate-bar" value={c.rate} size="sm" label={`${c.title}: ${formatPercent(c.rate, { lang })}`} />
                              <span className="stats-rate-text">{formatPercent(c.rate, { lang })}</span>
                            </span>
                          )}
                        </td>
                        {showMoney && (
                          <td className="ui-num" data-label={t('statistics.colRevenue')} title={money(c.revenue)}>{moneyCompact(c.revenue)}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Section>

        {/* ── Engagement ───────────────────────────────────────────────────── */}
        <Section id="engagement" title={t('statistics.sectionEngagement')}>
          {failed.engagement ? <SectionError onRetry={retry} /> : (
            <div className="kpi-row stats-engage">
              <MetricCard
                title={t('statistics.lessonViews')}
                value={count(engagement?.totalLessonViews)}
                change={inPeriod}
                icon={<Eye size={20} />}
                tone="info"
              />
              <MetricCard
                title={t('statistics.avgWatchTime')}
                value={watchMinutes ? t('statistics.minutesUnit', { count: formatNumber(watchMinutes, { lang }) }) : '—'}
                change={t('statistics.perLesson')}
                icon={<Timer size={20} />}
                tone="success"
              />
              <MetricCard
                title={t('statistics.assignmentCompletion')}
                value={hasValue(engagement?.assignmentCompletionRate) ? formatPercent(engagement.assignmentCompletionRate, { lang }) : '—'}
                change={inPeriod}
                icon={<FileText size={20} />}
                tone="brand"
              />
              <MetricCard
                title={t('statistics.examPassRate')}
                value={hasValue(engagement?.examPassRate) ? formatPercent(engagement.examPassRate, { lang }) : '—'}
                change={inPeriod}
                icon={<Target size={20} />}
                tone="purple"
              />
              <MetricCard
                title={t('statistics.feedbacks')}
                value={count(engagement?.feedbackCount)}
                change={inPeriod}
                icon={<MessageSquare size={20} />}
                tone="coral"
              />
              <MetricCard
                title={t('statistics.avgFeedbackRating')}
                value={engagement?.averageFeedbackRating ? Number(engagement.averageFeedbackRating).toFixed(1) : '—'}
                change={t('statistics.outOf5')}
                icon={<Smile size={20} />}
                tone="teal"
              />
            </div>
          )}
        </Section>
      </div>
    );
  }

  return (
    <div className="statistics-page">
      <PageHead
        icon={<BarChart3 />}
        title={t('statistics.title')}
        description={wholePlatform ? t('statistics.subtitle') : t('statistics.subtitleOwn')}
        actions={(
          <SubTabs
            className="stats-period"
            tabs={periodTabs}
            active={period}
            onChange={handlePeriod}
            ariaLabel={t('statistics.periodAria')}
          />
        )}
      />
      {body}
    </div>
  );
}

export default Statistics;
